/**
 * Impostazioni — le manopole del motore, in tre gruppi invece di nove schede.
 *
 * Erano nove sotto-tab. Due non erano impostazioni (**Articoli** e
 * **Semilavorati**: sono liste in lettura, e sono andate nel Fabbisogno, dove
 * servono); tre erano minuscole (Regole ha 2 righe, Setup 3, Famiglie 8).
 * Restano tre gruppi, che rispondono a tre domande diverse:
 *
 *  - **Le macchine** — cosa possono fare, e quanto;
 *  - **Stampi e velocità** — quanto costa cambiare, quanto si va veloci;
 *  - **Come ottimizzo** — cosa conta di più quando riordino.
 */
import { BarChart3, Check, Info } from "lucide-react";
import { useState } from "react";

import { RigaTesta } from "@/components/RigaTesta";
import {
  useM2Impostazioni,
  useM2PatchFamiglia,
  useM2PatchLinea,
  useM2PatchStampo,
  useM2PatchVincolo,
  useM2PutParametri,
  useM2PutSetup,
  useM2PutTransizione,
} from "@/hooks/useM2";
import { cn } from "@/lib/utils";
import { CAP_UM_LABEL, num } from "@/pages/M2/format";
import { StampoChip, StatoLineaBadge } from "@/pages/M2/shared";
import type {
  LineaRead,
  ParametriPreset,
  RegolaRead,
  RicettaFamigliaRead,
  SetupRead,
  StampoRead,
  TransizioneRead,
  VincoloLineaRead,
} from "@/types/m2";

type Gruppo = "macchine" | "stampi" | "ottimizzo";

const GRUPPI: [Gruppo, string, string][] = [
  ["macchine", "Le macchine", "Cosa possono fare, e quanto"],
  ["stampi", "Stampi e velocità", "Quanto costa cambiare, quanto si va veloci"],
  ["ottimizzo", "Come ottimizzo", "Cosa conta di più quando riordino"],
];

export function ImpostazioniM2() {
  const [gruppo, setGruppo] = useState<Gruppo>("macchine");
  const { data, isLoading } = useM2Impostazioni();

  const attivo = data?.parametri.find((p) => p.is_attivo);

  return (
    <div className="space-y-4">
      <RigaTesta
        contesto="le manopole del motore"
        destra="le modifiche valgono dalla prossima proposta"
      />

      <div className="grid gap-2 lg:grid-cols-3">
        {GRUPPI.map(([k, titolo, sub]) => (
          <button
            key={k}
            type="button"
            onClick={() => setGruppo(k)}
            className={cn(
              "rounded-lg border bg-card p-3 text-left transition-colors",
              gruppo === k ? "border-primary ring-1 ring-primary" : "hover:bg-accent/40"
            )}
          >
            <div className="text-[13px] font-semibold">{titolo}</div>
            <div className="mt-0.5 text-[11.5px] text-muted-foreground">{sub}</div>
          </button>
        ))}
      </div>

      {isLoading || !data ? (
        <p className="py-12 text-center text-sm text-muted-foreground">Caricamento…</p>
      ) : (
        <>
          {gruppo === "macchine" && (
            <div className="space-y-4">
              <SubLinee linee={data.linee} />
              <SubVincoli vincoli={data.vincoli} />
            </div>
          )}
          {gruppo === "stampi" && (
            <div className="space-y-4">
              <SubStampi stampi={data.stampi} transizioni={data.transizioni} />
              <SubFamiglie famiglie={data.ricette_famiglie} />
            </div>
          )}
          {gruppo === "ottimizzo" && (
            <div className="space-y-4">
              <SubPriorita preset={data.parametri} />
              <SubSetup setup={data.setup} />
              <SubRegole regole={data.regole} />
              {attivo && attivo.preset_nome !== "bilanciato" && (
                <FonteNota>
                  Il preset attivo è <b>{PRESET_LABEL[attivo.preset_nome] ?? attivo.preset_nome}</b>.
                  Se nessuno l&apos;ha scelto, è com&apos;è rimasto dal primo popolamento: vale la
                  pena confermarlo con chi pianifica.
                </FonteNota>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Titolo({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
      {children}
    </h2>
  );
}

function FonteNota({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border bg-muted px-3 py-2.5 text-xs text-muted-foreground">
      <BarChart3 className="size-3.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function NotaGiacomo({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-2.5 rounded-lg border border-brand-200 bg-accent px-3.5 py-2.5 text-[12.5px] text-accent-foreground dark:border-brand-800">
      <Info className="size-4 shrink-0 text-primary" />
      <div>{children}</div>
    </div>
  );
}

function Chip({ children, cls }: { children: React.ReactNode; cls: string }) {
  return <span className={cn("rounded px-2 py-0.5 text-xs", cls)}>{children}</span>;
}

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="switch"
      aria-checked={on}
      className={cn(
        "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
        on ? "bg-primary" : "bg-slate-300 dark:bg-slate-700"
      )}
    >
      <span
        className={cn(
          "inline-block size-4 rounded-full bg-white transition-transform",
          on ? "translate-x-4" : "translate-x-0.5"
        )}
      />
    </button>
  );
}

// ── Le macchine ──────────────────────────────────────────────────────

function SubLinee({ linee }: { linee: LineaRead[] }) {
  const patch = useM2PatchLinea();
  const estrusione = linee.filter((l) => l.tipo === "estrusione");
  const confez = linee.filter((l) => l.tipo === "confezionamento");

  const tabella = (gruppo: LineaRead[]) => (
    <div className="overflow-hidden rounded-lg border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-[10.5px] uppercase tracking-wide text-muted-foreground">
              <th className="px-3.5 py-2 text-left font-medium">Linea</th>
              <th className="px-3.5 py-2 text-left font-medium">Stato</th>
              <th className="px-3.5 py-2 text-right font-medium">Capacità</th>
              <th className="px-3.5 py-2 text-right font-medium">Giorni</th>
              <th className="px-3.5 py-2 text-right font-medium">Attiva</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {gruppo.map((l) => (
              <tr key={l.id}>
                <td className="px-3.5 py-2 font-medium">{l.nome ?? l.codice_macchina}</td>
                <td className="px-3.5 py-2">
                  <StatoLineaBadge stato={l.stato_rilevato} />
                </td>
                <td className="px-3.5 py-2 text-right tabular-nums">
                  {l.capacita_valore !== null && l.capacita_um
                    ? `~${num(l.capacita_valore, l.capacita_valore % 1 ? 1 : 0)} ${CAP_UM_LABEL[l.capacita_um]}`
                    : "—"}
                </td>
                <td className="px-3.5 py-2 text-right tabular-nums text-muted-foreground">
                  {l.giorni_attivi ?? "—"}
                </td>
                <td className="px-3.5 py-2 text-right">
                  <Toggle
                    on={l.is_attiva}
                    onClick={() => patch.mutate({ id: l.id, body: { is_attiva: !l.is_attiva } })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-2.5">
      <Titolo>Linee</Titolo>
      <FonteNota>Capacità = 90° percentile giornaliero dallo storico (gen→apr 2026).</FonteNota>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Estrusione
      </div>
      {tabella(estrusione)}
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Confezionamento
      </div>
      {tabella(confez)}
    </div>
  );
}

function SubVincoli({ vincoli }: { vincoli: VincoloLineaRead[] }) {
  const patch = useM2PatchVincolo();
  const perLinea = vincoli.reduce<Record<string, VincoloLineaRead[]>>((acc, v) => {
    (acc[v.linea_codice_macchina] ??= []).push(v);
    return acc;
  }, {});
  return (
    <div className="space-y-2.5">
      <Titolo>Quali stampi girano su quale macchina</Titolo>
      <FonteNota>
        Dedotti dallo storico: quali stampi sono effettivamente girati su quali macchine.
        L&apos;ottimizzatore li tratta come <b>duri</b> solo quando confermati.
      </FonteNota>
      {Object.entries(perLinea).map(([linea, vs]) => (
        <div key={linea} className="rounded-lg border bg-card p-3">
          <div className="mb-2 text-[12.5px] font-medium">Macchina {linea}</div>
          <div className="flex flex-wrap gap-2">
            {vs.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => patch.mutate({ id: v.id, body: { is_confermato: !v.is_confermato } })}
                className={cn(
                  "flex items-center gap-1 rounded-md border px-2 py-1 text-xs",
                  v.is_confermato ? "border-primary bg-accent" : "bg-card hover:bg-accent/40"
                )}
                title={`${v.n_osservazioni} osservazioni`}
              >
                <StampoChip stampo={v.valore} />
                {v.is_confermato && <Check className="size-3 text-primary" />}
              </button>
            ))}
          </div>
        </div>
      ))}
      <NotaGiacomo>
        <b>Da confermare con Giacomo:</b> le 2 macchine vincolate e l&apos;estrusore a uso speciale.
        Clicca uno stampo per confermarlo come vincolo duro su quella linea.
      </NotaGiacomo>
    </div>
  );
}

// ── Stampi e velocità ────────────────────────────────────────────────

function SubStampi({
  stampi,
  transizioni,
}: {
  stampi: StampoRead[];
  transizioni: TransizioneRead[];
}) {
  const patchStampo = useM2PatchStampo();
  const putTrans = useM2PutTransizione();
  const nonDefault = transizioni.filter((t) => t.minuti !== 40 || t.fonte !== "stima");
  return (
    <div className="space-y-3">
      <Titolo>Stampi (trafile)</Titolo>
      <FonteNota>
        <b>{stampi.length}</b> stampi — il driver del setup. La matrice dei tempi parte da una bozza
        uniforme di 40&apos; (fallback); Giacomo la affina.
      </FonteNota>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {stampi.map((s) => (
          <div key={s.id} className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2">
            <StampoChip stampo={s.codice} />
            <input
              defaultValue={s.descrizione_forma ?? ""}
              placeholder="descrizione forma…"
              onBlur={(e) => {
                if (e.target.value !== (s.descrizione_forma ?? ""))
                  patchStampo.mutate({ id: s.id, body: { descrizione_forma: e.target.value } });
              }}
              className="min-w-0 flex-1 rounded-md border bg-background px-2 py-1 text-sm"
            />
            <input
              defaultValue={s.max_rate_kg_h ? String(s.max_rate_kg_h) : ""}
              placeholder="max kg/h"
              title="Limite di velocità per trafila"
              onBlur={(e) => {
                const n = Number(e.target.value.replace(/\D/g, ""));
                if (n !== (s.max_rate_kg_h ?? 0))
                  patchStampo.mutate({ id: s.id, body: { max_rate_kg_h: n || null } });
              }}
              className="w-20 rounded-md border bg-background px-2 py-1 text-right text-sm"
            />
            <button
              type="button"
              onClick={() => patchStampo.mutate({ id: s.id, body: { is_confermato: !s.is_confermato } })}
              className={cn(
                "rounded px-1.5 py-0.5 text-[11px]",
                s.is_confermato
                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                  : "bg-secondary text-muted-foreground"
              )}
            >
              {s.is_confermato ? "✓" : "conferma"}
            </button>
          </div>
        ))}
      </div>
      <Titolo>Tempi da stampo a stampo</Titolo>
      {nonDefault.length === 0 && (
        <p className="text-[12.5px] text-muted-foreground">
          Tutte le transizioni sono a 40&apos; (bozza). Modificane una qui sotto per registrarne il
          tempo reale.
        </p>
      )}
      <div className="max-h-72 overflow-y-auto rounded-lg border bg-card">
        {transizioni.slice(0, 60).map((t) => (
          <div
            key={t.id}
            className="flex items-center gap-2 border-b px-3 py-1.5 text-sm last:border-b-0"
          >
            <StampoChip stampo={t.da_stampo} />
            <span className="text-muted-foreground">→</span>
            <StampoChip stampo={t.a_stampo} />
            <input
              defaultValue={String(t.minuti)}
              onBlur={(e) => {
                const n = Number(e.target.value.replace(/\D/g, ""));
                if (n && n !== t.minuti) putTrans.mutate({ id: t.id, minuti: n });
              }}
              className="ml-auto w-14 rounded-md border bg-background px-2 py-0.5 text-right text-sm"
            />
            <span className="text-xs text-muted-foreground">min</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const CONF_CLS: Record<string, string> = {
  alta: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  media: "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  bassa: "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300",
};

function SubFamiglie({ famiglie }: { famiglie: RicettaFamigliaRead[] }) {
  const patch = useM2PatchFamiglia();
  const righe = [...famiglie].sort((a, b) => (b.velocita_kg_h ?? 0) - (a.velocita_kg_h ?? 0));
  return (
    <div className="space-y-2.5">
      <Titolo>Velocità per famiglia-ricetta</Titolo>
      <FonteNota>
        Velocità di regime kg/h per macrofamiglia (90° percentile dello storico): la velocità reale a
        regime, non la media falsata dai cambi. <b>Sostituisce il t/g</b> come base di calcolo dei
        tempi — ed è il metro con cui si giudica se una durata del MES sta in piedi.
      </FonteNota>
      <div className="overflow-hidden rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3.5 py-2 text-left font-medium">Famiglia</th>
                <th className="px-3.5 py-2 text-left font-medium">Velocità regime</th>
                <th className="px-3.5 py-2 text-right font-medium">Campioni</th>
                <th className="px-3.5 py-2 text-left font-medium">Confidenza</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {righe.map((f) => (
                <tr key={f.id}>
                  <td className="px-3.5 py-2">
                    <span className="font-medium">{f.descrizione ?? f.prefisso}</span>{" "}
                    <span className="font-mono text-[11px] text-muted-foreground">{f.prefisso}</span>
                  </td>
                  <td className="px-3.5 py-2">
                    <input
                      defaultValue={f.velocita_kg_h ? String(f.velocita_kg_h) : ""}
                      onBlur={(e) => {
                        const n = Number(e.target.value.replace(/\D/g, ""));
                        if (n && n !== f.velocita_kg_h)
                          patch.mutate({ id: f.id, body: { velocita_kg_h: n } });
                      }}
                      className="w-24 rounded-md border bg-background px-2 py-1 text-right text-sm tabular-nums"
                    />{" "}
                    <span className="text-xs text-muted-foreground">kg/h</span>
                  </td>
                  <td className="px-3.5 py-2 text-right tabular-nums text-muted-foreground">
                    {num(f.n_campioni)}
                  </td>
                  <td className="px-3.5 py-2">
                    {f.confidenza && (
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[11px] font-medium",
                          CONF_CLS[f.confidenza]
                        )}
                      >
                        {f.confidenza}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <NotaGiacomo>
        <b>Da validare con Fabio:</b> SP (9.400 stimato vs 6.500 indicato) e CM (9.700 vs 7.000) — il
        p90 può catturare picchi non sostenibili. SM/GF/GS hanno pochi campioni. Il limite di
        velocità per trafila (es. T100 ≤ 3.000) è qui sopra, negli stampi.
      </NotaGiacomo>
    </div>
  );
}

// ── Come ottimizzo ───────────────────────────────────────────────────

const PRESET_LABEL: Record<string, string> = {
  bilanciato: "Bilanciato",
  massima_puntualita: "Massima puntualità",
  massima_efficienza: "Massima efficienza",
  priorita_scadenze: "Priorità scadenze",
};

function SubPriorita({ preset }: { preset: ParametriPreset[] }) {
  const put = useM2PutParametri();
  const attivo = preset.find((p) => p.is_attivo) ?? preset[0];
  if (!attivo) return null;

  const pesi: [keyof ParametriPreset, string, string][] = [
    [
      "peso_puntualita",
      "Puntualità sugli ordini",
      "Rispettare le consegne (ritardo = penalità pesante).",
    ],
    ["peso_cambi_stampo", "Minimizzare cambi stampo", "La leva forte: raggruppare per trafila."],
    ["peso_anticipo", "Evitare anticipo PF", "Penalità leggera: meglio poco anticipo che ritardo."],
    ["peso_saturazione", "Saturazione delle linee", "Sfruttare la capacità disponibile."],
  ];

  return (
    <div className="space-y-3">
      <Titolo>Preset e pesi</Titolo>
      <div className="flex flex-wrap gap-2">
        {preset.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => put.mutate({ preset_nome: p.preset_nome })}
            className={cn(
              "rounded-md border px-3.5 py-1.5 text-sm font-medium",
              p.is_attivo
                ? "border-primary bg-primary text-primary-foreground"
                : "bg-card hover:bg-accent/40"
            )}
          >
            {PRESET_LABEL[p.preset_nome] ?? p.preset_nome}
          </button>
        ))}
      </div>

      {pesi.map(([campo, titolo, desc]) => (
        <div key={campo as string}>
          <div className="mb-1.5 flex items-baseline justify-between">
            <span className="text-sm font-medium">{titolo}</span>
            <span className="text-xs font-semibold text-primary tabular-nums">
              {attivo[campo] as number}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            value={attivo[campo] as number}
            onChange={(e) =>
              put.mutate({ preset_nome: attivo.preset_nome, [campo]: Number(e.target.value) })
            }
            className="w-full accent-primary"
          />
          <div className="mt-1 text-xs text-muted-foreground">{desc}</div>
        </div>
      ))}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <NumField
          label="Finestra (giorni)"
          valore={attivo.finestra_giorni}
          onSave={(n) => put.mutate({ preset_nome: attivo.preset_nome, finestra_giorni: n })}
        />
        <NumField
          label="Rampa avvio (min)"
          valore={attivo.rampa_minuti}
          onSave={(n) => put.mutate({ preset_nome: attivo.preset_nome, rampa_minuti: n })}
        />
        <NumField
          label="Cambio stampo (min)"
          valore={attivo.minuti_cambio_stampo}
          onSave={(n) => put.mutate({ preset_nome: attivo.preset_nome, minuti_cambio_stampo: n })}
        />
      </div>

      <NotaGiacomo>
        La <b>finestra</b> è la manopola efficienza↔cadenza (default 2 settimane). La <b>rampa</b> è
        il tempo di avvio macchina (~1h) che si somma alla durata.
      </NotaGiacomo>
    </div>
  );
}

function NumField({
  label,
  valore,
  onSave,
}: {
  label: string;
  valore: number;
  onSave: (n: number) => void;
}) {
  const [val, setVal] = useState(String(valore));
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="mb-1 text-xs text-muted-foreground">{label}</div>
      <input
        value={val}
        onChange={(e) => setVal(e.target.value.replace(/\D/g, ""))}
        onBlur={() => {
          const n = Number(val);
          if (n && n !== valore) onSave(n);
        }}
        className="w-full rounded-md border bg-background px-2 py-1 text-sm"
      />
    </div>
  );
}

function SubSetup({ setup }: { setup: SetupRead[] }) {
  return (
    <div className="space-y-2.5">
      <Titolo>Tempi base di transizione</Titolo>
      <FonteNota>
        Tempi base per tipo di transizione. La matrice fine per coppia di stampi è nel gruppo
        «Stampi e velocità».
      </FonteNota>
      <div className="rounded-lg border bg-card">
        {setup.map((t) => (
          <SetupRow key={t.id} setup={t} />
        ))}
      </div>
    </div>
  );
}

function SetupRow({ setup }: { setup: SetupRead }) {
  const put = useM2PutSetup();
  const [val, setVal] = useState(String(setup.minuti));
  return (
    <div className="grid grid-cols-[1fr_120px_1fr] items-center gap-3 border-b px-4 py-3 last:border-b-0">
      <div>
        <div className="font-medium">{setup.etichetta}</div>
        {setup.descrizione && (
          <div className="text-xs text-muted-foreground">{setup.descrizione}</div>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <input
          value={val}
          onChange={(e) => setVal(e.target.value.replace(/\D/g, ""))}
          onBlur={() => {
            const n = Number(val);
            if (n !== setup.minuti) put.mutate({ id: setup.id, minuti: n });
          }}
          className="w-16 rounded-md border bg-background px-2 py-1 text-right text-sm"
        />
        <span className="text-sm text-muted-foreground">min</span>
      </div>
      <div className="text-xs text-muted-foreground">
        {setup.fonte === "manuale" ? "✓ confermato" : "stima"}
      </div>
    </div>
  );
}

function SubRegole({ regole }: { regole: RegolaRead[] }) {
  return (
    <div className="space-y-2.5">
      <Titolo>Regole di sequenza</Titolo>
      <FonteNota>
        Regole <b>sugli attributi</b>, non prodotto per prodotto. Doc #2: la bonifica è
        de-prioritizzata (costo ≈ 0); il driver è il cambio stampo.
      </FonteNota>
      {regole.map((r) => (
        <div
          key={r.id}
          className="rounded-lg border border-brand-200 bg-accent p-3.5 text-sm dark:border-brand-800"
        >
          <div className="font-medium text-accent-foreground">{r.nome}</div>
          {r.descrizione && (
            <div className="mt-1 text-[13px] text-accent-foreground/90">{r.descrizione}</div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <Chip cls="border bg-card">{r.azione}</Chip>
            {r.costo_minuti > 0 && (
              <Chip cls="bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300">
                {r.costo_minuti}&apos;
              </Chip>
            )}
            <Chip
              cls={
                r.is_attiva
                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                  : "bg-secondary"
              }
            >
              {r.is_attiva ? "attiva" : "disattiva"}
            </Chip>
          </div>
        </div>
      ))}
    </div>
  );
}
